import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { PageHeader, Card, Button, Alert } from '../components/ui';

const Settings = () => {
  const { user, changePassword } = useAuth();
  
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  const handleChangePassword = async (e) => {
    e.preventDefault();
    
    // Reset messages
    setError('');
    setSuccess('');
    
    // Validate passwords
    if (!currentPassword || !newPassword || !confirmPassword) {
      setError('All fields are required');
      return;
    }
    
    if (newPassword !== confirmPassword) {
      setError('New passwords do not match');
      return;
    }
    
    if (newPassword.length < 8) {
      setError('New password must be at least 8 characters long');
      return;
    }
    
    setLoading(true);
    
    try {
      const result = await changePassword(currentPassword, newPassword);
      
      if (result) {
        setSuccess('Password changed successfully');
        // Clear form
        setCurrentPassword('');
        setNewPassword('');
        setConfirmPassword('');
      } else {
        setError('Failed to change password');
      }
    } catch (err) {
      setError('An error occurred while changing password');
      console.error('Change password error:', err);
    } finally {
      setLoading(false);
    }
  };
  
  return (
    <div>
      <PageHeader 
        title="Settings" 
        description="Manage your account settings"
      />
      
      <div className="space-y-6">
        <Card title="Account Information">
          <div className="space-y-4">
            <div>
              <h4 className="text-sm font-medium text-gray-500">Username</h4>
              <p className="mt-1 text-sm text-gray-900">{user?.username || 'Admin'}</p>
            </div>
            <div>
              <h4 className="text-sm font-medium text-gray-500">Email</h4>
              <p className="mt-1 text-sm text-gray-900">{user?.email || '—'}</p>
            </div>
            <div>
              <h4 className="text-sm font-medium text-gray-500">Role</h4>
              <p className="mt-1 text-sm text-gray-900">{user?.role || 'Administrator'}</p>
            </div>
          </div>
        </Card>
        
        <Card title="Change Password">
          {error && (
            <Alert 
              variant="error" 
              className="mb-4"
              isDismissible
              onDismiss={() => setError('')}
            >
              {error}
            </Alert>
          )}
          
          {success && (
            <Alert 
              variant="success" 
              className="mb-4"
              isDismissible
              onDismiss={() => setSuccess('')}
            >
              {success}
            </Alert>
          )}
          
          <form onSubmit={handleChangePassword} className="space-y-4">
            <div>
              <label htmlFor="currentPassword" className="block text-sm font-medium text-gray-700">
                Current Password
              </label>
              <input
                type="password"
                id="currentPassword"
                name="currentPassword"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                disabled={loading}
              />
            </div>
            
            <div>
              <label htmlFor="newPassword" className="block text-sm font-medium text-gray-700">
                New Password
              </label>
              <input
                type="password"
                id="newPassword"
                name="newPassword"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                disabled={loading}
              />
            </div>
            
            <div>
              <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700">
                Confirm New Password
              </label>
              <input
                type="password"
                id="confirmPassword"
                name="confirmPassword"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                disabled={loading}
              />
            </div>
            
            <div className="pt-2">
              <Button
                type="submit"
                variant="primary"
                isLoading={loading}
                disabled={loading}
              >
                Change Password
              </Button>
            </div>
          </form>
        </Card>
      </div>
    </div>
  );
};

export default Settings;
